/**
 * Stuck Sync Warning Component
 *
 * Displays a warning banner when responses have failed to sync
 * and provides a manual retry button.
 */

import React from 'react';
import { Text, StyleSheet, Platform } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { Banner, useTheme } from 'react-native-paper';
import { useOfflineSync } from '../hooks/useOfflineSync';

interface StuckSyncWarningProps {
  reviewId?: number;
  style?: StyleProp<ViewStyle>;
}

export const StuckSyncWarning: React.FC<StuckSyncWarningProps> = ({
  reviewId,
  style,
}) => {
  const theme = useTheme();
  const [visible, setVisible] = React.useState(true);
  const [retrying, setRetrying] = React.useState(false);

  const {
    failedResponses,
    retryNeededCount,
    syncInProgress,
    forceSync,
  } = useOfflineSync();

  // Only show on mobile
  if (Platform.OS === 'web') {
    return null;
  }

  // Only show if sync needs attention. Normal pending work syncs on the
  // periodic timer and should not look like a warning.
  const retryCount = retryNeededCount ?? failedResponses;
  const hasStuckItems = retryCount > 0;

  React.useEffect(() => {
    if (hasStuckItems && !visible) {
      setVisible(true);
    } else if (!hasStuckItems && visible) {
      setVisible(false);
    }
  }, [hasStuckItems, retryCount, visible]);

  if (!hasStuckItems || !visible) {
    return null;
  }

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await forceSync();
      // Auto-hide after successful sync
      setTimeout(() => setVisible(false), 2000);
    } catch (error) {
      console.error('[StuckSyncWarning] Manual retry failed:', error);
    } finally {
      setRetrying(false);
    }
  };

  const getMessage = () => {
    if (retryCount > 0) {
      return `${retryCount} item${retryCount !== 1 ? 's' : ''} failed to sync. Tap retry to sync again.`;
    }
    return 'Some items need attention before they can sync.';
  };

  return (
    <Banner
      visible={visible}
      actions={[
        {
          label: 'Dismiss',
          onPress: () => setVisible(false),
        },
        {
          label: retrying ? 'Retrying...' : 'Retry Sync',
          onPress: handleRetry,
          disabled: retrying || syncInProgress,
        },
      ]}
      icon="alert-circle"
      style={[
        styles.banner,
        {
          backgroundColor: theme.colors.errorContainer,
        },
        style,
      ]}
    >
      <Text style={[styles.message, { color: theme.colors.onSurface }]}>
        {getMessage()}
      </Text>
    </Banner>
  );
};

const styles = StyleSheet.create({
  banner: {
    marginHorizontal: 0,
    marginBottom: 8,
  },
  message: {
    fontSize: 14,
  },
});
